import { JepGraphic } from '@jeppesen-foreflight/jmkw-types';
import * as stringUtils from '../../../../utilities/string-format.utils';
import { NavigationFeatureType } from '../navigation-feature-type';
import { FeatureDetail } from './feature-detail';
import { AirspaceAttributesRaw } from './airspace-attributes-raw';
import { AirspaceAttributes } from './airspace-attributes';

export class Airspace implements FeatureDetail {
  id: string;
  navType: NavigationFeatureType = 'airspace';
  attributes: AirspaceAttributes;

  constructor(public graphic: JepGraphic) {
    if (!graphic) {
      throw new Error('Graphic is required to create an Airspace instance');
    }

    this.id = this._getAirspaceDisambigName(graphic.attributes);
    this.attributes = this._formatAirspaceAttributes(graphic.attributes);
  }

  private _formatAirspaceAttributes(
    attrs: AirspaceAttributesRaw
  ): AirspaceAttributes {
    return {
      airspaceClass: stringUtils.formatString(attrs.aspclass),
      airspaceDesignator: stringUtils.formatString(attrs.airspacedesignator),
      airspaceTypeDescription: stringUtils.formatString(
        attrs.asptypedescription
      ),
      boundaryClassDescription: stringUtils.formatString(
        attrs.boundaryclassdescription
      ),
      clearanceApprovalAgency: stringUtils.formatString(
        attrs.clearanceapprovalagency
      ),
      controlledBy: stringUtils.formatString(attrs.airspacecontrolledby),
      id: this.id,
      name: stringUtils.formatString(attrs.aspname),
      lowerLimit: this._formatLimit(attrs.lowerlimitft, attrs.lowlimitref),
      opnote: this._formatOpnote(attrs.opnote),
      sectorId: stringUtils.formatString(attrs.sectorid),
      timesOfOperation: stringUtils.formatString(attrs.timesofoperation),
      upperLimit: this._formatLimit(attrs.upperlimitft, attrs.uplimitref)
    };
  }

  private _formatLimit(limit?: number | null, ref?: string | null): string {
    if (limit === undefined || limit === null) {
      return stringUtils.formatString(ref) || '';
    }
    return `${stringUtils.formatMeasurement(limit, '')} ${stringUtils.formatString(ref) || ''}`.trim();
  }

  private _formatOpnote(opnote?: string | null): string[] | null {
    const note = stringUtils.formatString(opnote);
    return note ? note.split('<OPNOTE>') : null;
  }

  private _getAirspaceDisambigName(props: AirspaceAttributesRaw): string {
    const SPECIAL_TYPES = [
      'OCA',
      'CCA',
      'CTA',
      'CTL',
      'UTA',
      'UCA',
      'UTL',
      'CEA',
      'CGA',
      'ATZ',
      'CFA',
      'MTZ',
      'CTR',
      'CTZ',
      'MTR',
      'TIA',
      'TIZ',
      'SRA',
      'SRZ',
      'CAA',
      'CBA',
      'TCA',
      'CDA',
      'MMA',
      'MTC',
      'TMA',
      'PCA'
    ];

    const aspType = stringUtils.formatString(props?.asptype);
    const aspName = stringUtils.formatString(props?.aspname);
    const aspClass = stringUtils.formatString(props?.aspclass);
    const sectorId = stringUtils.formatString(props?.sectorid);
    const aspIdent = stringUtils.formatString(props?.aspident);
    const displayLowerAlt = stringUtils.formatString(props?.displayloweralt);
    const displayUpperAlt = stringUtils.formatString(props?.displayupperalt);

    // Early return for empty case
    if (!aspName && !aspIdent) {
      return '';
    }

    if (aspType === 'BTH') {
      return `${aspName || aspIdent} FIR/UIR`;
    }

    if (['FIR', 'UIR'].includes(aspType)) {
      return `${aspName || aspIdent} ${aspType}`;
    }

    if (SPECIAL_TYPES.includes(aspType)) {
      // Use aspIdent as fallback when aspName is empty
      let name = aspName || aspIdent;

      if (aspClass) {
        name += ` Class ${aspClass}`;
      }

      if (sectorId === 'Z') {
        name += ' Perimeter';
      } else {
        name += ` (${displayLowerAlt}/${displayUpperAlt})`;
      }

      return name;
    }

    return aspName || aspIdent;
  }
}
